import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  CloudOff, RefreshCw, Trash2, Play, CheckCircle2, XCircle, 
  Clock, AlertTriangle, Loader2, Database, Cloud
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = import.meta.env.VITE_API_URL || (window.location.origin + "/api");

export default function SyncQueuePage() {
  const [queueItems, setQueueItems] = useState([]);
  const [stats, setStats] = useState({ pending: 0, processing: 0, completed: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [isApiLimitReached, setIsApiLimitReached] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/sync-queue`);
      if (response.data.success) {
        setQueueItems(response.data.items || []);
        setStats(response.data.stats || { pending: 0, processing: 0, completed: 0, failed: 0 });
        setIsApiLimitReached(response.data.isApiLimitReached || false);
      }
    } catch (error) {
      console.error("Error fetching queue:", error);
      toast.error("Failed to load sync queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
    // Refresh every 30 seconds
    const interval = setInterval(fetchQueue, 30000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  const handleProcessQueue = async () => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API}/sync-queue/process`);
      if (response.data.success) {
        toast.success(`Processed ${response.data.processed} items`);
        fetchQueue();
      } else {
        if (response.data.limitReached) {
          toast.error("Zoho API limit still reached. Please try again later.");
          setIsApiLimitReached(true);
        } else {
          toast.error(response.data.error || "Processing failed");
        }
      }
    } catch (error) {
      console.error("Process error:", error);
      toast.error(error.response?.data?.error || "Failed to process queue");
    } finally {
      setProcessing(false);
    }
  };

  const handleRetryItem = async (itemId) => {
    try {
      const response = await axios.post(`${API}/sync-queue/retry/${itemId}`);
      if (response.data.success) {
        toast.success("Item queued for retry");
        fetchQueue();
      }
    } catch (error) {
      toast.error("Failed to retry item");
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      const response = await axios.delete(`${API}/sync-queue/${itemId}`);
      if (response.data.success) {
        toast.success("Item deleted");
        fetchQueue();
      }
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  const handleClearCompleted = async () => {
    try {
      const response = await axios.post(`${API}/sync-queue/clear-completed`);
      if (response.data.success) {
        toast.success(`Cleared ${response.data.deleted} completed items`);
        fetchQueue();
      }
    } catch (error) {
      toast.error("Failed to clear completed items");
    } finally {
      setShowClearDialog(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      "pending": "bg-amber-100 text-amber-700 border-amber-200",
      "processing": "bg-blue-100 text-blue-700 border-blue-200",
      "completed": "bg-green-100 text-green-700 border-green-200",
      "failed": "bg-red-100 text-red-700 border-red-200"
    };
    return styles[status] || "bg-slate-100 text-slate-600";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending": return <Clock className="w-4 h-4" />;
      case "processing": return <Loader2 className="w-4 h-4 animate-spin" />;
      case "completed": return <CheckCircle2 className="w-4 h-4" />;
      case "failed": return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="sync-queue-page">
      {/* Header */}
      <motion.div 
        className="mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-poppins bg-gradient-to-r from-vcc-teal to-vcc-pink bg-clip-text text-transparent flex items-center gap-3">
              <Database className="w-8 h-8 text-vcc-teal" />
              Sync Queue
            </h1>
            <p className="text-slate-500 font-poppins mt-1">
              Manage offline operations waiting to sync with Zoho
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={fetchQueue}
              variant="outline"
              className="font-poppins"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {stats.completed > 0 && (
              <Button
                onClick={() => setShowClearDialog(true)}
                variant="outline"
                className="font-poppins text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Completed
              </Button>
            )}
          </div>
        </div>
      </motion.div>

      {/* API Status Banner */}
      {isApiLimitReached && (
        <motion.div 
          className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <CloudOff className="w-6 h-6 text-amber-600" />
          <div className="flex-1">
            <p className="font-poppins font-medium text-amber-800">
              Zoho API Limit Reached
            </p>
            <p className="text-sm text-amber-600 font-poppins">
              Operations are being queued locally and will sync automatically when the API becomes available.
            </p>
          </div>
          <Button
            onClick={handleProcessQueue}
            disabled={processing}
            variant="outline"
            className="font-poppins border-amber-300 text-amber-700 hover:bg-amber-100"
          >
            {processing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-2" />
            )}
            Retry Sync
          </Button>
        </motion.div>
      )}

      {/* Stats Cards */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-poppins text-slate-800">{stats.pending}</p>
                <p className="text-xs text-slate-500 font-poppins">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Loader2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-poppins text-slate-800">{stats.processing}</p>
                <p className="text-xs text-slate-500 font-poppins">Processing</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-poppins text-slate-800">{stats.completed}</p>
                <p className="text-xs text-slate-500 font-poppins">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-poppins text-slate-800">{stats.failed}</p>
                <p className="text-xs text-slate-500 font-poppins">Failed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Process Queue Button */}
      {stats.pending > 0 && !isApiLimitReached && (
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Cloud className="w-6 h-6 text-blue-600" />
                <div>
                  <p className="font-poppins font-medium text-blue-800">
                    {stats.pending} operations ready to sync
                  </p>
                  <p className="text-sm text-blue-600 font-poppins">
                    Click process to sync all pending operations to Zoho
                  </p>
                </div>
              </div>
              <Button
                onClick={handleProcessQueue}
                disabled={processing}
                className="bg-blue-600 hover:bg-blue-700 text-white font-poppins"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Process Queue
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Queue Table */}
      <motion.div 
        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-vcc-teal animate-spin mb-4" />
            <p className="text-slate-500 font-poppins">Loading queue...</p>
          </div>
        ) : queueItems.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle2 className="w-12 h-12 mx-auto text-green-400 mb-4" />
            <p className="text-slate-600 font-poppins font-medium mb-2">Queue is empty</p>
            <p className="text-sm text-slate-400 font-poppins">
              All operations are synced with Zoho
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="font-poppins font-semibold">Status</TableHead>
                  <TableHead className="font-poppins font-semibold">Operation</TableHead>
                  <TableHead className="font-poppins font-semibold">Entity</TableHead>
                  <TableHead className="font-poppins font-semibold">Sample No</TableHead>
                  <TableHead className="font-poppins font-semibold">Created</TableHead>
                  <TableHead className="font-poppins font-semibold">Retries</TableHead>
                  <TableHead className="font-poppins font-semibold">Error</TableHead>
                  <TableHead className="font-poppins font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {queueItems.map((item) => (
                  <TableRow key={item.id} className="hover:bg-slate-50">
                    <TableCell>
                      <Badge className={`${getStatusBadge(item.status)} font-poppins border flex items-center gap-1 w-fit`}>
                        {getStatusIcon(item.status)}
                        {item.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-poppins text-slate-700 font-medium">
                        {item.operationType}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-poppins">
                        {item.entityType}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-poppins text-vcc-teal font-medium">
                        {item.sampleNo || "-"}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-500 font-poppins">
                        {formatDate(item.createdAt)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-poppins text-slate-600">
                        {item.retryCount || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      {item.lastError && (
                        <span className="text-xs text-red-500 font-poppins max-w-[200px] truncate block" title={item.lastError}>
                          {item.lastError.substring(0, 50)}...
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {(item.status === "failed" || item.status === "pending") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRetryItem(item.id)}
                            className="font-poppins text-blue-600"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteItem(item.id)}
                          className="font-poppins text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </motion.div>

      {/* Clear Completed Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-poppins">Clear Completed Items?</AlertDialogTitle>
            <AlertDialogDescription className="font-poppins">
              This will remove all {stats.completed} completed items from the queue. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-poppins">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleClearCompleted}
              className="bg-red-600 hover:bg-red-700 font-poppins"
            >
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
